import { css, html, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import { PageContent } from 'src/pages/page-content.js';
import { t } from 'src/i18n';
import 'src/utilities/display-alert';
import '../../content/d-entity-header.js';
import '../../library/components/d-help-section.js';
import '../../library/elements/d-action.js';
import type { PageViewModel } from '@trinnvis/dabih-api-client';
import { features } from '../../store/selectors/features/features';
import { uuid } from 'src/utilities/text';
import type { PageLink } from 'src/pages/overview-page/overview-page-view-model';
import type { RiskAssessmentsPageViewModel } from './risk-assessments-page-view-model';
import type { RiskAssessmentGroup } from './risk-assessment-group';
import type { CreateEntityInput } from '../../layout/parts/create-entity-input';
import { pageIds } from 'src/store/selectors/pages/pageIds';

/**
 *
 */
@customElement('d-risk-assessments-page-content')
export class DRiskAssessmentsPageContent extends PageContent<RiskAssessmentsPageViewModel> {
  static readonly styles = [
    ...super.styles,
    css`
      .page-links {
        margin-bottom: 12px;
        border-bottom: 1px solid var(--borderColorOnGray);
      }
      .columns {
        column-count: 3;
        column-width: 400px;
        column-gap: 20px;
      }
      .box {
        position: relative;
        -webkit-column-break-inside: avoid;
        page-break-inside: avoid;
        break-inside: avoid;
        box-shadow: 0 0 10px hsla(1, 0%, 0%, 0.2);
        margin-bottom: 20px;
        background: white;
        border-radius: 12px;
        padding: 0 16px 10px 16px;
        line-height: 130%;
      }
      /* WebKit/Safari paints a box-shadow across the full width of a CSS
         multi-column container, so the box's bottom shadow is re-painted at the
         top of the next column, leaving a phantom shadow in the empty columns.
         Drop the shadow on WebKit only until the layout is revisited (a designer
         is reviewing). The hanging-punctuation feature query matches Safari and
         the Capacitor WKWebView, but not Chrome or Firefox. */
      @supports (hanging-punctuation: first) {
        .box {
          box-shadow: none;
        }
      }
      .header {
        display: flex;
        flex-direction: row-reverse;
        justify-content: space-between;
        flex-wrap: wrap;
        gap: 8px;
        border-top-left-radius: 12px;
        border-top-right-radius: 12px;
        position: sticky;
        padding-top: 16px;
        background: white;
        z-index: 1;
      }
      .theme-name {
        flex-grow: 1;
        font-size: 22px;
        line-height: 120%;
        font-weight: 500;
        margin-bottom: 6px;
        word-wrap: break-word;
        overflow-wrap: break-word;
        hyphens: auto;
        min-width: 0;
      }
      .add {
        flex: none;
        display: flex;
        cursor: pointer;
        margin-top: 4px;
      }
      .add-text {
        margin-top: -1px;
        color: var(--themeColor);
        font-size: 14px;
        padding-right: 8px;
        opacity: 0;
      }
      .plus {
        position: relative;
        width: 16px;
        height: 16px;
      }
      .plus > div {
        position: absolute;
        top: 6.5px;
        width: 16px;
        height: 3px;
        background: var(--themeColor);
      }
      .plus > div:last-child {
        transform: rotate(90deg);
      }
      d-list-section {
        margin: 8px 0;
      }
      @media (hover: hover) {
        .add:hover > .add-text {
          opacity: 1;
        }
        .add:hover d-action {
          display: block;
        }
      }
    `,
  ];
  @property({ type: Array })
  pages: PageViewModel[] = [];
  @property({ type: Boolean, attribute: 'write-access', reflect: true })
  writeAccess = false;
  @property({ type: Boolean })
  editMode = false;
  @property({ type: Object })
  token = {};
  @property({ type: Boolean })
  uncoverPageMenu = false;
  @property({ type: String })
  key = '';
  @property({ type: Number })
  organizationId = 0;

  renderPageLinks(list: PageLink[]) {
    if (list.length) {
      return html` <div class="page-links">
        ${list.map((pageLink: PageLink) => {
          return html`<a class="page-link" style="${this.pageLinkStyle(pageLink.type)}" href="${pageLink.href}"
            >${pageLink.name}</a
          >`;
        })}
      </div>`;
    }
    return nothing;
  }

  renderContent() {
    if (this.pageView.promoData) {
      return html` <d-promo-section
        theme-page
        .employeesCount=${this.pageView.employeesCount}
        .specialTerms=${this.pageView.specialTerms}
        .promoData=${this.pageView.promoData}
        .features=${features}
        .canUpgrade=${this.writeAccess}
      ></d-promo-section>`;
    }
    return html` ${this.renderPageLinks(this.pageView.pageLinks)}
      <div class="columns">
        ${this.pageView.riskAssessments.map((r) => {
          return html` <div class="box">
            <div class="header" style="top: ${this.contentStickyTop - 4}px">
              <div>
                ${r.themePageTarget === 'page' && r.themePageId !== 0 && r.themePageId !== pageIds.emergency
                  ? html` <div class="add" @click=${() => this.create(r.themePageId)}>
                      <div class="add-text">${t('riskAssessments.add')}</div>
                      <div class="plus">
                        <div></div>
                        <div></div>
                      </div>
                    </div>`
                  : r.themePageTarget === 'substance'
                    ? html`<d-action plain href="${this.pageView.substancesPageHref}"
                        >${unsafeHTML(t('riskAssessments.goToSubstances'))}</d-action
                      >`
                    : r.themePageTarget === 'dataItem'
                      ? html`<d-action plain href="${this.pageView.dataItemsPageHref}"
                          >${unsafeHTML(t('riskAssessments.goToDataDocumentation'))}</d-action
                        >`
                      : r.themePageId === pageIds.emergency
                        ? html`<d-action plain href="${this.pageView.emergencyPageHref}"
                            >${unsafeHTML(t('riskAssessments.goToEmergency'))}</d-action
                          >`
                        : nothing}
              </div>
              <div class="theme-name">${r.themeName}</div>
            </div>
            ${r.items.length
              ? html` <d-list-section
                  no-header
                  no-left-padding
                  .items=${r.items}
                  organization-id="${this.pageView.organizationId}"
                  .writeAccess=${this.writeAccess}
                  .contentStickyTop=${this.contentStickyTop}
                ></d-list-section>`
              : nothing}
          </div>`;
        })}
      </div>`;
  }

  private create(pageId: number | undefined) {
    if (pageId) {
      const id = uuid();
      const targetUrl = this.pageView.href + 'riskAssessments/' + id + '?edit';
      this.dispatchEvent(
        new CustomEvent<CreateEntityInput>('create-entity', {
          bubbles: true,
          composed: true,
          detail: {
            entityType: 'riskAssessments',
            entityUuid: id,
            pageId: pageId,
            targetUrl,
          },
        }),
      );
    }
  }

  private renderHeaderAction(r: RiskAssessmentGroup) {
    if (r.substanceRelated) {
      return html`<d-action plain href="${this.pageView.substancesPageHref}"
        >${unsafeHTML(t('riskAssessments.goToSubstances'))}</d-action
      >`;
    }
    if (r.themePageId) {
      return html`<div class="add" @click=${() => this.create(r.themePageId)}>
        <div class="add-text">${t('riskAssessments.add')}</div>
        <div class="plus">
          <div></div>
          <div></div>
        </div>
      </div>`;
    }
    return html``;
  }

  private pageLinkStyle(type: string) {
    return 'background-image: url(/images/' + type + '-color.svg)';
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'd-risk-assessments-page-content': DRiskAssessmentsPageContent;
  }
}
